/* istanbul ignore file */
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';
import Extent from '@arcgis/core/geometry/Extent';
import * as esriProjection from '@arcgis/core/geometry/projection';
import Graphic from '@arcgis/core/Graphic';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer';
import LabelClass from '@arcgis/core/layers/support/LabelClass';

import { AppConstants } from 'cd-eft/appConstants';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { LinearUnit, convertLinearUnit } from 'cd-eft/utils/structureLayerUtils';
import sleep from 'cd-eft/utils/sleep';

import { RouteComponentProps } from 'react-router-dom';

import {
  ElevationInterpolater,
} from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine';
import { showCoordinates } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/loadLayers';

import {
  SurfaceTinElevationLayer,
  SurfaceTin,
  SurfaceTinFace,
  SurfaceTinNode,
} from 'cd-eft/framework/layers/SurfaceTinElevationLayer';
import * as JsonToSurface from 'cd-eft/services/JsonToSurface/JsonToSurface';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import { getElevationUsingContours } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';

const { projectsLibraryStore, designStore } = stores.AppStore;
const tinNodeSymbol = new SimpleMarkerSymbol({ color: 'black', size: 3 });
const tinFillSymbol = new SimpleFillSymbol({
  color: 'transparent',
  style: 'solid',
  outline: {
    color: [127, 127, 127, 255],
    width: '0.4',
  },
});
// const contourLineSymbol = new SimpleLineSymbol({ color: 'blue', width: '1px' /* '1.5px' */ });
const contourMajorSymbol = new SimpleLineSymbol({ color: [255, 127, 127, 1.0], width: '2px' });
const contourMinorSymbol = new SimpleLineSymbol({ color: [255, 127, 127, 1.0], width: '1px' });
const contourRenderer = new UniqueValueRenderer({
  field: 'major',
  defaultSymbol: contourMinorSymbol,
  uniqueValueInfos: [
    {
      value: 1,
      symbol: contourMajorSymbol,
    },
  ],
});

const contourLabels = new LabelClass({
  where: 'major = 1',
  symbol: {
    type: 'text', // autocasts as new TextSymbol()
    color: [0, 112, 255, 1.0], // [255, 127, 127, 1.0],
    font: {
      // autocast as new Font()
      family: 'Arial Unicode MS',
      size: 11,
      weight: 'bold',
    },
    haloColor: [246, 246, 246, 1.0],
    haloSize: 2,
  },
  labelPlacement: 'center-along',
  labelPosition: 'parallel',
  repeatLabel: true,
  repeatLabelDistance: 500, // 3000,
  labelExpressionInfo: {
    expression: '$feature.elevation',
  },
});

export class TinContourSurface {
  private static readonly defaultMapWkid: number = 3857;
  private static readonly defaultMapElevUnit: LinearUnit = 'meters';

  private designedSurface: boolean;
  private mapView: MapView;
  private mapCrs: SpatialReference;
  private mapElevUnit: LinearUnit;

  private tinData?: SurfaceTin;
  private contourData?: Graphic[];
  private origContourLines?: any[];
  private tinExtent?: Extent;

  public contourInterval: number = 2.0;
  public contourRatio: number = 5;
  private surfaceType?: string;
  history: RouteComponentProps['history'];

  constructor(history: RouteComponentProps['history'], view: MapView, elevUnit: LinearUnit | undefined = undefined, designed: boolean) {
    this.mapView = view;
    this.mapCrs = this.mapView.spatialReference || new SpatialReference({ wkid: TinContourSurface.defaultMapWkid });
    this.mapElevUnit = elevUnit || TinContourSurface.defaultMapElevUnit;
    this.history = history;
    this.designedSurface = designed;
  }

  public hasData(): boolean {
    // Assume surfaceType is a class member variable that you set when processing data
    if (this.surfaceType === 'Surface-Dem') {
      return true;
    }

    return undefined !== this.tinData && 0 < this.tinData.faces.length;
  }

  public async retrieveOriginalSurface(
    sessionUuid: string,
    dataWkid: number,
    dataElevUnit: LinearUnit
  ): Promise<boolean> {
    this.tinData = undefined;
    this.tinExtent = undefined;
    this.contourData = undefined;
    this.origContourLines = undefined;

    let surfaceTinId = 0;
    let noSurveysFlag = false;

    this.surfaceType = designStore.currentSurface.surfaceDefClassName === 'survey' ? 'Surface' : 'Surface-Dem'

    try {
      const surfaceDesignElements = await SETAPI.DesignElementController.getAllDesignElementsBySessionAndTypeUsingGET(
        sessionUuid,
        this.surfaceType
      );
      if (surfaceDesignElements && surfaceDesignElements.data && 0 < surfaceDesignElements.data.length) {
        surfaceTinId = surfaceDesignElements.data[0].designElementId!;
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }

    if (0 < surfaceTinId) {
      try {
        const response = await SETAPI.DesignElementController.surveyDesignElementByIdUsingGET(surfaceTinId.toString());
        const responseData = response.data;
        if (responseData[0] && responseData[0].json) {
          const surfaceResult = JSON.parse(responseData[0].json!);
          designStore.surveyPoints = surfaceResult.cgPoints;
          if (!surfaceResult.borderVector.length) {
            noSurveysFlag = true;
          } else {
            await this.generateSurfaceFromData(
              dataWkid,
              dataElevUnit,
              this.surfaceType === 'Surface' ? surfaceResult.tinTriangles : [], // If surfaceType is 'Surface-Dem', use an empty array
              this.surfaceType === 'Surface' ? surfaceResult.cgPoints : [], // If surfaceType is 'Surface-Dem', use an empty array
              surfaceResult.contourLines
            );
          }
        }
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      }
    }

    if (surfaceTinId === 0 || noSurveysFlag) {
      this.history.push(`/design/${designStore.projectsStore.currentProject.projectUuid}/surface`);
    }
    return this.hasData()

  }

  public async retrieveMultiStructureSurface(
    sessionUuid: string,
    structureIds: number[],
    dataWkid: number,
    dataElevUnit: LinearUnit
  ): Promise<boolean> {
    this.tinData = undefined;
    this.tinExtent = undefined;
    this.contourData = undefined;
    this.origContourLines = undefined;

    this.surfaceType = designStore.currentSurface.surfaceDefClassName === 'survey' ? 'Surface' : 'Surface-Dem'
    try {
      const surfaceRequest = {
        sessionId: sessionUuid,
        structureDesElemIds: structureIds,
        includeContour: true,
      };
      const response = await SETAPI.StructureController.getMultipleDesignSurfacesUsingPOST(surfaceRequest);
      const surfaceResult = response.data;

      if (this.surfaceType) {
        await this.generateSurfaceFromData(
          dataWkid,
          dataElevUnit,
          this.surfaceType === 'Surface' ? surfaceResult.designedTinTriangles! : [], // If surfaceType is 'Surface-Dem', use an empty array
          undefined, //it is only for survey not dem
          surfaceResult.designedContours
        );
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }

    return this.hasData()// === false ? true : true;
  }

  public async generateSurfaceFromData(
    dataWkid: number,
    dataElevUnit: LinearUnit,
    tinTriangles: any[],
    cgPoints: any[] | undefined = undefined,
    contours: any[] | undefined = undefined,
    ensureTin: boolean = false
  ): Promise<boolean> {
    // console.log('generateSurfaceFromData', contours);

    this.tinData = undefined;
    this.tinExtent = undefined;
    this.contourData = undefined;
    this.origContourLines = undefined;

    if (ensureTin || (designStore.currentSurface.surfaceDefClassName === 'survey')) {
      let tin: SurfaceTin;
      if (undefined === cgPoints) {
        tin = this.generateTinNoPoints(dataWkid, dataElevUnit, tinTriangles);
      } else {
        tin = this.generateTinWithPoints(dataWkid, dataElevUnit, tinTriangles, cgPoints);
      }

      this.tinData = await tin.project(this.mapView.spatialReference.wkid, this.mapElevUnit);
      this.tinExtent = this.tinData?.computeExtent();
    }

    if (contours) {
      this.contourData = await this.generateContourGraphics(contours, dataWkid, dataElevUnit);
      this.origContourLines = contours;
    }

    return this.hasData();
  }

  generateTinWithPoints(dataWkid: number, dataElevUnit: LinearUnit, tinTriangles: any[], cgPoints: any[]): SurfaceTin {
    const tinResult = new SurfaceTin(dataWkid, dataElevUnit);

    designStore.surveyPointNames = [];
    for (const cgPoint of cgPoints) {
      designStore.surveyPointNames.push(cgPoint.name);
      const node = new SurfaceTinNode(cgPoint.x, cgPoint.y, cgPoint.z);
      tinResult.nodes.push(node);
    }

    for (const tinTriangle of tinTriangles) {
      //NOTE: returned point indexes are 1-based, convert to 0-based index
      const face = new SurfaceTinFace(tinTriangle.tin.p1 - 1, tinTriangle.tin.p2 - 1, tinTriangle.tin.p3 - 1);
      tinResult.faces.push(face);
    }

    return tinResult;
  }

  generateTinNoPoints(dataWkid: number, dataElevUnit: LinearUnit, tinTriangles: any[]): SurfaceTin {
    const tinResult = new SurfaceTin(dataWkid, dataElevUnit);

    type RawPoint = { name: number; x: number; y: number; z: number };
    const rawPoints: RawPoint[] = [];

    for (const t of tinTriangles) {
      const p1Name = t.tin.p1 as number;
      let p1Index = rawPoints.findIndex((p) => {
        return p1Name === p.name;
      });
      if (0 > p1Index) {
        p1Index = rawPoints.length;
        const point = t.points[p1Name.toString()];
        rawPoints.push({ name: p1Name, x: point.x, y: point.y, z: point.z });
      }

      const p2Name = t.tin.p2 as number;
      let p2Index = rawPoints.findIndex((p) => {
        return p2Name === p.name;
      });
      if (0 > p2Index) {
        p2Index = rawPoints.length;
        const point = t.points[p2Name.toString()];
        rawPoints.push({ name: p2Name, x: point.x, y: point.y, z: point.z });
      }

      const p3Name = t.tin.p3 as number;
      let p3Index = rawPoints.findIndex((p) => {
        return p3Name === p.name;
      });
      if (0 > p3Index) {
        p3Index = rawPoints.length;
        const point = t.points[p3Name.toString()];
        rawPoints.push({ name: p3Name, x: point.x, y: point.y, z: point.z });
      }

      const face = new SurfaceTinFace(p1Index, p2Index, p3Index);
      tinResult.faces.push(face);
    }

    for (const p of rawPoints) {
      const node = new SurfaceTinNode(p.x, p.y, p.z);
      tinResult.nodes.push(node);
    }

    return tinResult;
  }

  async generateContourGraphics(contourLines: any[], dataWkid: number, dataElevUnit: LinearUnit): Promise<Graphic[]> {
    this.surfaceType = designStore.currentSurface.surfaceDefClassName === 'survey' ? 'Surface' : 'Surface-Dem'
    if (dataWkid !== this.mapCrs.wkid) {
      await esriProjection.load();
    }

    const result: Graphic[] = [];
    const dataCsr: SpatialReference = projectsLibraryStore.projectSpatialReference!;

    let oid = 1;

    let extent: Extent | undefined = undefined;
    for (const contour of contourLines) {
      try {
        const isMajor: boolean = 0 === Math.floor(contour.elevation / this.contourInterval + 0.001) % this.contourRatio;

        const line = TinContourSurface.convertContourToLine(
          contour,
          dataCsr,
          convertLinearUnit(contour.elevation, dataElevUnit, this.mapElevUnit)
        );
        const line2 = dataWkid === this.mapCrs.wkid ? line : (esriProjection.project(line, this.mapCrs) as Polyline);
        if (extent === undefined) {
          extent = line2.extent;
        } else {
          extent = extent.union(line2.extent);
        }
        const graphic = new Graphic({
          geometry: line2,
          symbol: isMajor ? contourMajorSymbol : contourMinorSymbol,
          attributes: { OBJECTID: oid, elevation: Math.round(contour.elevation), major: isMajor ? 1 : 0 },
        });
        result.push(graphic);
        oid++;
      } catch (error) {
        console.error('Error: ', (error as Error).message);
        console.error('Contour: ', contour);
      }
    }
    if (this.surfaceType === 'Surface-Dem') {
      this.tinExtent = extent; // this only for DEM surface
    }
    return result;
  }

  static convertContourToLine(contour: any, spatialReference: SpatialReference, lineZ: number): Polyline {
    const points: number[][] = [];
    for (const point of contour.points) {
      points.push([point.x, point.y, lineZ]);
    }
    const line = new Polyline();
    line.spatialReference = spatialReference;
    line.hasZ = true;
    line.addPath(points);
    return line;
  }

  public async displaySurface(dataWkid: number, waitForScene: boolean, layerOffset: number = 1, ensureTin: boolean = false): Promise<boolean> {
    let result: boolean = false;
    if (this.mapView) {
      const map = this.mapView.map;
      if (map) {
        await esriProjection.load();

        // get breakline layer and clear it
        const breaklineLayer = map.findLayerById(AppConstants.BreaklineLayerId) as GraphicsLayer;
        if (breaklineLayer) {
          breaklineLayer.removeAll();
        }

        const designedContourLayer = map.findLayerById(AppConstants.DesignedContourLayerId) as FeatureLayer;
        if (designedContourLayer) {
          const removedLayer = map.remove(designedContourLayer);
          removedLayer.destroy();
        }
        // let contourLayer = map.findLayerById(AppConstants.ContourLinesLayerId) as GraphicsLayer;
        // if (contourLayer) {
        //   contourLayer.removeAll();
        // } else {
        //   contourLayer = TinContourSurface.createGraphicsLayer(AppConstants.ContourLinesLayerId, 'Contours');
        //   map.add(contourLayer, layerOffset);
        // }
        // if (undefined !== this.contourData) {
        //   contourLayer.addMany(this.contourData);
        //   result = true;
        // }

        if ((this.surfaceType === 'Surface-Dem') && (!ensureTin)) {
          const contourId = this.designedSurface ? AppConstants.DesignedContourLayerId : AppConstants.OriginalContourLayerId;
          let offset = this.designedSurface ? layerOffset + 1 : layerOffset;
          offset = map.findLayerById('usdaNedBestAvailable') ? offset + 1 : offset;
          const contourTitle = this.designedSurface ? 'Designed Contours' : 'Original Contours';
          let contourLayer = map.findLayerById(contourId) as FeatureLayer;
          if (contourLayer) {
            const removedLayer = map.remove(contourLayer);
            removedLayer.destroy();
          }
          if (undefined !== this.contourData && this.contourData.length > 0) {
            contourLayer = TinContourSurface.createFeatureLayer(
              contourId,
              contourTitle,
              this.mapCrs,
              this.contourData
            );
            map.add(contourLayer, offset);
            result = true;
          }
          if (undefined !== this.contourData) {
            result = true;
          }

          if (this.designedSurface) {
            const originalContourLayer = map.findLayerById(AppConstants.OriginalContourLayerId);
            if (originalContourLayer) originalContourLayer.visible = false;
          } else {
            const designedContourLayer = map.findLayerById(AppConstants.DesignedContourLayerId);
            if (designedContourLayer) {
              map.remove(designedContourLayer);
              designedContourLayer.destroy();
            }
            const originalContourLayer = map.findLayerById(AppConstants.OriginalContourLayerId);
            if (originalContourLayer) originalContourLayer.visible = true;
          }

          // *** Set up on-the-fly elevation interpolation  ***/
          SetupInterpolateTinElevation(this.surfaceType, this.mapView);
        }

        let trianglesLayer: GraphicsLayer | undefined;
        let surveyPointsLayer: GraphicsLayer | undefined;
        if (ensureTin || (this.surfaceType === 'Surface')) {
          const contourId = this.designedSurface ? AppConstants.DesignedContourLayerId : AppConstants.OriginalContourLayerId;
          const contourTitle = this.designedSurface ? 'Designed Contours' : 'Original Contours';
          let contourLayer = map.findLayerById(contourId) as FeatureLayer;
          if (contourLayer) {
            const removedLayer = map.remove(contourLayer);
            removedLayer.destroy();
          };
          if (undefined !== this.contourData && this.contourData.length > 0) {
            contourLayer = TinContourSurface.createFeatureLayer(
              contourId,
              contourTitle,
              this.mapCrs,
              this.contourData
            );
            map.add(contourLayer, layerOffset);
            result = true;

            // if (this.designedSurface && (this.surfaceType !== 'Surface')) {
            if (this.designedSurface) {
              const originalContourLayer = map.findLayerById(AppConstants.OriginalContourLayerId);
              if (originalContourLayer) originalContourLayer.visible = false;
            }
          }

          if (undefined !== this.contourData) {
            // const additions: Graphic[] = this.contourData;
            // contourLayer.applyEdits({ addFeatures: additions });
            // contourLayer.source.addMany(this.contourData);
            result = true;
          }

          const tinVisible = stores.AppStore.designStore.sceneViewActive;
          const tinId = this.designedSurface ? AppConstants.DesignedTinTrianglesLayerId : AppConstants.OriginalTinTrianglesLayerId;
          const tinTitle = this.designedSurface ? 'Designed TIN Triangles' : 'Original TIN Triangles';
          const offset = this.designedSurface ? layerOffset + 2 : layerOffset + 1;
          trianglesLayer = map.findLayerById(tinId) as GraphicsLayer;
          const originalTinLayer = map.findLayerById(AppConstants.OriginalTinTrianglesLayerId) as GraphicsLayer;
          if (trianglesLayer) {
            trianglesLayer.removeAll();
          } else {
            trianglesLayer = TinContourSurface.createGraphicsLayer(tinId, tinTitle);
            map.add(trianglesLayer, offset);
          }
          trianglesLayer.visible = tinVisible;
          if (this.hasData()) {
            TinContourSurface.populateFacesLayer(trianglesLayer, this.tinData!);
            result = true;

            if (this.designedSurface) {
              if (originalTinLayer) originalTinLayer.visible = false;
            } else {
              const designedTinLayer = map.findLayerById(AppConstants.DesignedTinTrianglesLayerId);
              if (designedTinLayer) {
                map.remove(designedTinLayer);
                designedTinLayer.destroy();
              }
              if (originalTinLayer) originalTinLayer.visible = tinVisible;
            }
          }

          surveyPointsLayer = map.findLayerById(AppConstants.SurveyPointsLayerId) as GraphicsLayer;
          if (surveyPointsLayer) {
            surveyPointsLayer.removeAll();
          } else {
            surveyPointsLayer = TinContourSurface.createGraphicsLayer(AppConstants.SurveyPointsLayerId, 'Survey Points');
            map.add(surveyPointsLayer, layerOffset + 3);
          }
          if (this.hasData()) {
            TinContourSurface.populateNodesLayer(surveyPointsLayer, this.tinData!);
            surveyPointsLayer.visible = false; // (this.surfaceType === 'Surface');
            result = true;
          }

          //always remove stale demHillshadeLayer
          let demHillshadeLayer = map.findLayerById('DEM-HILLSHADE') as GroupLayer;
          if (demHillshadeLayer) {
            map.remove(demHillshadeLayer);
          }
          // only add demHillshadeLayer if surfaceExpressMode === false
          if (designStore.surfaceExpressMode === false) {
            if (this.origContourLines) {
              demHillshadeLayer = await JsonToSurface.getDemAndHillshadeLayerFromContours(
                this.origContourLines,
                projectsLibraryStore.projectSpatialReference!
              );
              demHillshadeLayer.id = 'DEM-HILLSHADE';
              map.add(demHillshadeLayer, 0);
            }
          }
        }

        if (this.surfaceType === 'Surface') {
          // Important!!! load the elevation interpolater singleton whenever invoking the Surface Generation service.
          ElevationInterpolater.Load(trianglesLayer, surveyPointsLayer);

          // UODATE MAP ELEVATION LAYER TO THE CURRENT SURFACE
          //remove previously loaded SurfaceTinElevationLayer
          if (this.mapView.map.ground) {
            const oldLayer = this.mapView.map.ground.layers.find((item) => {
              return item.id === AppConstants.SurfaceTinElevationLayerId;
            });
            if (oldLayer) {
              this.mapView.map.ground.layers.remove(oldLayer);
            }
          }

          // *** Set up on-the-fly elevation interpolation  ***/
          SetupInterpolateTinElevation(this.surfaceType, this.mapView);
        }

        if (this.hasData()) {
          if (this.surfaceType === 'Surface') {
            // create and add the ElevationLayer
            const elevationLayer = new SurfaceTinElevationLayer(this.tinData!);
            elevationLayer.id = AppConstants.SurfaceTinElevationLayerId;
            // this.mapView.map.ground.layers.add(elevationLayer); // we have to make this condtional if its survey we should excute this if not excute the next line        
            this.mapView.map.ground.layers.add(elevationLayer);
          }

          //update the Home extent to the surface extent
          designStore.setHomeWidgetTargetExtent(this.tinExtent!);
          // This is an ugly way to make sure the scene view is created
          // and ready to display the map in a synchronized manner
          let counter = 0;
          while (waitForScene) {
            if (stores.AppStore.surveyEngineeringStore.gisModel.sceneView || 5 < counter) {
              break;
            }
            await sleep(1000);
            counter++;
          }
          if (this.tinExtent) {
            this.mapView.goTo(this.tinExtent);
          }
        }
      } else {
        MessageCenter.addError('Map object is not defined.');
      }
    } else {
      MessageCenter.addError('Map view object is not defined.');
    }
    return result;
  }

  static createGraphicsLayer(layerId: string, layerTitle: string) {
    const layer = new GraphicsLayer();
    layer.id = layerId;
    layer.title = layerTitle;
    return layer;
  }

  private static createFeatureLayer(
    layerId: string,
    layerTitle: string,
    mapCrs: SpatialReference,
    features: Graphic[]
  ): FeatureLayer {
    const layer = new FeatureLayer({
      id: layerId,
      title: layerTitle,
      spatialReference: mapCrs,
      fields: [
        {
          name: 'OBJECTID',
          type: 'oid',
        },
        {
          name: 'elevation',
          type: 'integer',
        },
        {
          name: 'major',
          type: 'integer',
        },
      ],
      objectIdField: 'OBJECTID',
      displayField: 'elevation',
      hasZ: true,
      source: features,
      renderer: contourRenderer,
      labelingInfo: [contourLabels],
    });
    return layer;
  }

  static populateFacesLayer(layer: GraphicsLayer, surfaceTin: SurfaceTin): void {
    const spatialReference = new SpatialReference({ wkid: surfaceTin.wkid });
    for (const face of surfaceTin.faces) {
      try {
        const polygon = TinContourSurface.convertFaceToPolygon(face, surfaceTin.nodes, spatialReference);
        const graphic = new Graphic({ geometry: polygon, symbol: tinFillSymbol });
        layer.add(graphic);
      } catch (error) {
        console.error('error:', error);
        console.error('face:', face);
      }
    }
  }

  static convertFaceToPolygon(
    face: SurfaceTinFace,
    nodes: SurfaceTinNode[],
    spatialReference: SpatialReference
  ): Polygon {
    const node1 = nodes[face.nodeIndex1];
    const node2 = nodes[face.nodeIndex2];
    const node3 = nodes[face.nodeIndex3];
    if ((node1 === undefined) || (node2 === undefined) || (node3 === undefined)) {
      console.warn('INVALID TIN TRIANGLE:');
      console.warn(face);
      throw new Error('Invalid TIN triangle with missing node(-s) detected');
    }
    const point1 = TinContourSurface.convertNodeToPoint(node1, spatialReference);
    const point2 = TinContourSurface.convertNodeToPoint(node2, spatialReference);
    const point3 = TinContourSurface.convertNodeToPoint(node3, spatialReference);
    const polygon = new Polygon();
    polygon.spatialReference = spatialReference;
    polygon.hasZ = true;
    polygon.addRing([point1, point2, point3]);
    return polygon;
  }

  static populateNodesLayer(layer: GraphicsLayer, surfaceTin: SurfaceTin): void {
    const spatialReference = new SpatialReference({ wkid: surfaceTin.wkid });
    for (const node of surfaceTin.nodes) {
      const point = TinContourSurface.convertNodeToPoint(node, spatialReference);
      const graphic = new Graphic({ symbol: tinNodeSymbol, geometry: point });
      layer.add(graphic);
    }
  }

  static convertNodeToPoint(node: SurfaceTinNode, spatialReference: SpatialReference): Point {
    const point = new Point();
    point.spatialReference = spatialReference;
    point.hasZ = true;
    point.x = node.x;
    point.y = node.y;
    point.z = node.z;
    return point;
  }

}

export function SetupInterpolateTinElevation(surfaceType, mapView) {
  const dataCrs = projectsLibraryStore.projectSpatialReference!;
  if (designStore.coordinateWatchHandler) designStore.coordinateWatchHandler.remove();
  designStore.coordinateWatchHandler = undefined;
  designStore.coordinateWatchHandler = mapView.on('pointer-move', async (event) => {
    try {
      const mapGeometry = mapView.toMap({ x: event.x, y: event.y });
      const dataGeometry = esriProjection.project(mapGeometry, dataCrs) as Point;

      const z = surfaceType === 'Surface' ? ElevationInterpolater.Current?.Interpolate(dataGeometry.x, dataGeometry.y, mapGeometry) : await getElevationUsingContours(dataGeometry, mapGeometry, mapView);

      showCoordinates({
        x: dataGeometry.x,
        y: dataGeometry.y,
        z: z,
      });
    } catch (error) {
      //log to output, but otherwise do nothing, non-critical error
      console.error(error);
    }
  });
}
